import React, {FunctionComponent, StrictMode, useEffect} from 'react'
import {Main} from '@App/Layouts/Main'
import {TriangleDivider} from '@App/Components/TriangleDivider'
import {ContactUs} from "@App/Components/ContactUs";
import {BadgeCheckIcon} from "@heroicons/react/solid";
import {useScrollToTop} from "@App/hooks/useScrollToTop";
import {MailIcon, PhoneIcon} from "@heroicons/react/outline";
import {useRedirectBack} from "@App/hooks/useRedirectBack";
import {ServiceFAQs} from "@App/Components/ServiceFAQs";
import {useLocation, useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from "@App/state";
import {ServiceCardInfo} from "@App/Pages/Partner/LeasePlan/ServiceCardInfo";
import {useTranslation} from "react-i18next";
import {ServiceIsVehicleReturn} from "@App/utils/conditionals/services";
import {
    OrganizationIsAyvens,
    OrganizationIsLeaseplan,
    OrganizationIsRepareo
} from "@App/utils/conditionals/organization";
import {phone, phoneLink} from "@App/utils/phoneHelper";
import {bindActionCreators} from "@reduxjs/toolkit";

export type SummaryProps = {
    embedded?: boolean
}

export const Summary: FunctionComponent<SummaryProps> = ({embedded = false}: SummaryProps) => {


    const dispatch = useDispatch();
    const {
        setMileage,
        setNumberPlate,
    } = bindActionCreators(actionCreators, dispatch)

    const {t} = useTranslation()
    const navigate = useNavigate()
    useScrollToTop()
    const organization = useSelector((state: State) => state.orders.organization)
    const service = useSelector((state: State) => state.orders.service)
    const userID = useSelector((state: State) => state.auth.userId)

    // redirect back to start if parameters are missing
    const {redirectOnMissingParams} = useRedirectBack()
    const location = useLocation()
    useEffect(() => {
        redirectOnMissingParams(['garage', 'service', 'place', 'slot'], location.pathname.substring(0, location.pathname.indexOf('/werkstaetten')))
    }, [])

    const backToVehicleSelection = () => {
        setMileage(0)
        setNumberPlate( {firstID: '', secondID: '', region: ''} )
        navigate('/partner/ayvens/fahrzeug')
    }

    return (
        <StrictMode>
            <Main>
                <div
                    className="bg-white flex items-center justify-center flex-col max-w-3xl mx-auto">
                    <div className={'mb-4 py-8'}>
                        <BadgeCheckIcon className={'w-32 h-32 text-primary'}/>
                    </div>
                    <div className={'mb-3'}>
                        <div className="text-2xl mb-2">
                            {service && ServiceIsVehicleReturn(service) ? t('summary.vehicleReturn.text') : t('summary.text')}
                        </div>
                        <div className={'mb-3'}>
                            {
                                OrganizationIsRepareo(organization) ? t('summary.contact-us-repareo') :
                                    (service && ServiceIsVehicleReturn(service)
                                        ? t('summary.vehicleReturn.contact-us-partner')
                                        : t('summary.contact-us-partner')
                                    )
                            }
                        </div>
                        <div>
                            <div className="flex gap-1 items-center mb-2">
                                <PhoneIcon className={'text-primary mr-1 w-5 h-6'}/>
                                <div>
                                    <a href={phoneLink(organization, t)}>{phone(organization, t)}</a>
                                    <div className="text-neutral-500 whitespace-pre-line">{
                                        OrganizationIsRepareo(organization) && t('contact.reachability-repareo')
                                        || OrganizationIsLeaseplan(organization) && t('contact.reachability-leaseplan')
                                        || t('contact.reachability-partner')
                                    }</div>
                                </div>
                            </div>
                            <div className="flex gap-1 items-center mb-2">
                                <MailIcon className={'text-primary mr-1 w-5 h-6'}/>
                                <a href="mailto:werkstattmeister@repareo.de">werkstattmeister@repareo.de</a>
                            </div>
                        </div>
                        {organization.slug === 'leaseplan' && (
                            <div className="mt-9">
                                <ServiceCardInfo/>
                            </div>
                        )}
                        {(OrganizationIsAyvens(organization) && userID) && (
                            <div
                            onClick={backToVehicleSelection}>
                        <div
                            className={`bg-primary w-full md:w-80 px-8 py-2 mt-4 text-white rounded text-center font-serif font-bold cursor-pointer no-underline`}>
                            {t('driverDataStep.backToVehicleSelection')}
                        </div>
                    </div>
                        )}
                    </div>
                </div>
            </Main>
            {!embedded && (
                <section className="bg-neutral-100 pb-10">
                    <TriangleDivider direction={"right"} background={"bg-white"}/>
                    <div className="container md:flex gap-10 mt-10">
                        <div className="w-full md:w-1/2">
                            <ServiceFAQs/>
                        </div>
                        <div className="w-full md:w-1/2">
                            <ContactUs/>
                        </div>
                    </div>
                </section>
            )}
        </StrictMode>
    )
}
